import * as React from 'react';
import { Input } from '@/components/ui/Input';
import { useMerchantSuggestions } from '@/hooks/useMerchantSuggestions';
import type { MerchantSuggestion } from '@/types';

interface MerchantAutocompleteProps {
  value: string;
  onChange: (merchant: string) => void;
  onSelectSuggestion?: (suggestion: MerchantSuggestion) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function MerchantAutocomplete({
  value,
  onChange,
  onSelectSuggestion,
  label = 'Merchant (optional)',
  placeholder = 'e.g., Amazon, Whole Foods',
  disabled,
}: MerchantAutocompleteProps) {
  const [open, setOpen] = React.useState(false);
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { data: suggestions = [], isFetching } = useMerchantSuggestions(value);

  const showDropdown = open && value.trim().length >= 2 && (isFetching || suggestions.length > 0);

  React.useEffect(() => {
    setHighlightIndex(0);
  }, [suggestions, value]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectSuggestion = (suggestion: MerchantSuggestion) => {
    onChange(suggestion.merchant);
    onSelectSuggestion?.(suggestion);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) {
        setOpen(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIndex]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        label={label}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
      />

      {showDropdown && (
        <div
          className="absolute z-50 mt-1 w-full rounded-lg border border-input bg-background shadow-md"
          role="listbox"
        >
          {isFetching && suggestions.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>
          )}
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.merchant}-${index}`}
              type="button"
              role="option"
              aria-selected={index === highlightIndex}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted ${
                index === highlightIndex ? 'bg-muted' : ''
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectSuggestion(suggestion)}
            >
              <span>{suggestion.merchant}</span>
              {suggestion.lastCategory && (
                <span className="ml-2 text-xs text-muted-foreground">{suggestion.lastCategory}</span>
              )}
            </button>
          ))}
          {!isFetching && value.trim().length >= 2 && (
            <div className="border-t border-input px-3 py-2 text-xs text-muted-foreground">
              Press Enter to use “{value.trim()}” as a new merchant
            </div>
          )}
        </div>
      )}
    </div>
  );
}
